/**
 * New Relic Logger Utility
 * Centralized logging service for the application
 */

// New Relic Browser Agent will be loaded via script tag in index.html
declare global {
  interface Window {
    newrelic?: {
      noticeError: (
        error: Error | string,
        customAttributes?: Record<string, unknown>
      ) => void
      setCustomAttribute: (
        name: string,
        value: string | number | boolean
      ) => void
      setUserId: (userId: string) => void
      addPageAction: (
        name: string,
        attributes?: Record<string, unknown>
      ) => void
      recordMetric: (name: string, value: number) => void
      getContext: () => Record<string, unknown>
    }
    NREUM?: {
      noticeError?: (
        error: Error | string,
        customAttributes?: Record<string, unknown>
      ) => void
      setCustomAttribute?: (
        name: string,
        value: string | number | boolean
      ) => void
      loader?: {
        errors?: unknown[]
      }
    }
  }
}

// Lista de errores de New Relic que podemos ignorar silenciosamente
const IGNORED_NR_ERRORS = ['ChunkLoadError', 'Loading chunk', 'nr-spa', 'nrWrapper']

/**
 * Verifica si un error debe ser ignorado
 */
function shouldIgnoreError(error: Error | string): boolean {
  const errorMessage =
    typeof error === 'string' ? error : error.message || error.name || ''
  return IGNORED_NR_ERRORS.some((ignored) => errorMessage.includes(ignored))
}

class Logger {
  /**
   * Logs an error to New Relic
   */
  logError(error: Error | string, context?: Record<string, unknown>): void {
    try {
      if (shouldIgnoreError(error)) {
        return
      }

      if (window.newrelic) {
        const errorObj = typeof error === 'string' ? new Error(error) : error

        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            try {
              window.newrelic?.setCustomAttribute(key, String(value))
            } catch {
              // Silently ignore attribute setting errors
            }
          })
        }

        window.newrelic.noticeError(errorObj, context)
      }
    } catch {
      // Silently ignore logging failures
    }
  }

  /**
   * Sets user information in New Relic
   */
  setUser(userId: string, userName?: string, userEmail?: string): void {
    try {
      if (window.newrelic) {
        window.newrelic.setUserId(userId)
        if (userName) {
          window.newrelic.setCustomAttribute('userName', userName)
        }
        if (userEmail) {
          window.newrelic.setCustomAttribute('userEmail', userEmail)
        }
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Adds custom attributes for tracking
   */
  setCustomAttribute(name: string, value: string | number | boolean): void {
    try {
      if (window.newrelic) {
        window.newrelic.setCustomAttribute(name, value)
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Adds multiple custom attributes at once
   */
  setCustomAttributes(
    attributes: Record<string, string | number | boolean>
  ): void {
    try {
      if (window.newrelic) {
        Object.entries(attributes).forEach(([key, value]) => {
          try {
            window.newrelic?.setCustomAttribute(key, value)
          } catch {
            // Silently ignore individual attribute errors
          }
        })
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Records a custom page action/event
   */
  addPageAction(name: string, attributes?: Record<string, unknown>): void {
    try {
      if (window.newrelic) {
        window.newrelic.addPageAction(name, attributes)
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Records a custom metric
   */
  recordMetric(name: string, value: number): void {
    try {
      if (window.newrelic) {
        window.newrelic.recordMetric(name, value)
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Logs API request information
   */
  logApiRequest(method: string, url: string, data?: unknown): void {
    try {
      if (window.newrelic) {
        window.newrelic.addPageAction('api_request', {
          method,
          url,
          hasData: !!data
        })
      }
    } catch {
      // Silently ignore
    }
  }

  /**
   * Logs API error information
   */
  logApiError(
    method: string,
    url: string,
    status: number,
    errorData?: unknown
  ): void {
    try {
      const context = {
        apiMethod: method,
        apiUrl: url,
        apiStatus: status,
        errorData: errorData ? JSON.stringify(errorData) : undefined
      }

      this.logError(
        new Error(`API Error: ${method} ${url} - Status ${status}`),
        context
      )
    } catch {
      // Silently ignore
    }
  }
}

/**
 * Detecta si un error fue causado por el agente de New Relic (nrWrapper)
 * y no por la aplicación en sí.
 */
export function isNewRelicAgentError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message || ''
  const stack = error.stack || ''
  return msg.includes('nrWrapper') || stack.includes('nrWrapper')
}

export const logger = new Logger()
export default logger
